/**
 * Active filter chips for the Website Audit table.
 *
 * Shows each active filter as a removable pill. "Clear all" button
 * on the right when any filter/sort/search is active.
 */
import React from "react"

import { useWebsiteAudit } from "../../context/WebsiteAuditContext"
import { THEME } from "../../config/theme"

const FILTER_LABELS: Record<string, string> = {
  rooftop: "Rooftop",
  vehicle_type: "Condition",
}

export default function WebsiteFilterChips() {
  const {
    filters,
    setFilter,
    sort,
    setSort,
    search,
    setSearch,
    clearAll,
  } = useWebsiteAudit()

  const activeFilters: Array<{ key: keyof typeof filters; label: string; value: string }> = []
  ;(Object.keys(filters) as Array<keyof typeof filters>).forEach((key) => {
    filters[key].forEach((value) => {
      activeFilters.push({ key, label: FILTER_LABELS[key] ?? key, value })
    })
  })

  const hasSearch = search.trim() !== ""
  const hasSort = sort !== null
  const hasAnything = activeFilters.length > 0 || hasSearch || hasSort

  if (!hasAnything) return null

  const removeFilter = (key: keyof typeof filters, value: string) => {
    setFilter(
      key,
      filters[key].filter((v) => v !== value),
    )
  }

  const chipStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: THEME.spacing.xs,
    padding: `2px ${THEME.spacing.sm}`,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: "rgba(13, 26, 66, 0.08)",
    color: THEME.colors.navy,
    borderRadius: THEME.borderRadius.pill,
    border: `0.5px solid rgba(13, 26, 66, 0.3)`,
  }

  const removeButtonStyle: React.CSSProperties = {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: THEME.colors.navy,
    fontSize: 14,
    padding: 0,
    lineHeight: 1,
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: `${THEME.spacing.sm} 0`,
        flexWrap: "wrap",
      }}
    >
      {activeFilters.map((af, idx) => (
        <span key={`${af.key}-${af.value}-${idx}`} style={chipStyle}>
          {af.label}: {af.value}
          <button
            type="button"
            onClick={() => removeFilter(af.key, af.value)}
            style={removeButtonStyle}
            aria-label={`Remove ${af.label} filter ${af.value}`}
          >
            ×
          </button>
        </span>
      ))}
      {hasSearch && (
        <span style={chipStyle}>
          Search: {search}
          <button
            type="button"
            onClick={() => setSearch("")}
            style={removeButtonStyle}
            aria-label="Clear search"
          >
            ×
          </button>
        </span>
      )}
      {hasSort && (
        <span style={chipStyle}>
          Sort: {sort.field} {sort.direction}
          <button
            type="button"
            onClick={() => setSort(null)}
            style={removeButtonStyle}
            aria-label="Clear sort"
          >
            ×
          </button>
        </span>
      )}
      <button
        type="button"
        onClick={clearAll}
        style={{
          marginLeft: "auto",
          padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
          fontSize: 12,
          fontWeight: 500,
          border: `1px solid ${THEME.colors.silver}`,
          borderRadius: THEME.borderRadius.md,
          backgroundColor: THEME.colors.neutral.light.surface,
          color: THEME.colors.neutral.light.textPrimary,
          fontFamily: THEME.fonts.primary,
          cursor: "pointer",
        }}
      >
        Clear all
      </button>
    </div>
  )
}
